import { Avatar, Box, Column, useDisclosure } from "@hightouch/htui";
import { noop } from "lodash";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Skeleton } from "src/components/loading";
import { UserMenu } from "src/components/layout/header/user-menu";
import { NavItem } from "src/components/layout/nav/nav-item";
import { useUser } from "src/contexts/user-context";
import { More } from "src/components/layout/nav/more";
import { Icon as SettingsIcon } from "src/components/layout/nav/icons/settings";
import { ActiveSectionProps } from "src/components/layout/nav/utils";
import { Settings } from "src/components/layout/nav/sections/settings";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import { NavItemText } from "src/components/layout/nav/nav-item-text";
import { useSpaceContext } from "src/components/spaces";
import { ChatSupport } from "./chat-support";
import { DevTools } from "./dev-tools";
import { PoweredBy } from "./powered-by";
import { Resources } from "./resources";

export const Footer = (props: ActiveSectionProps) => {
  const { user } = useUser();
  const { organizationLogoCobrandingHidden } = useFlags();
  const { hasAccess: hasOrgSettings } = useHasOrganizationSettings();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const userMenu = useDisclosure();
  const showPoweredBy =
    user?.organization?.logo_url && !organizationLogoCobrandingHidden;

  const showSettings = !isInSpace || isPathAccessibleInSpace("/settings");

  return (
    <Column mt={2} px={2}>
      <More />

      <Column gap={3}>
        <Resources />
        {/* Pylon only loads in non-dev builds, so Chat support has no function
            in dev — hide it there (dev shows the Dev tools item instead). */}
        {!import.meta.env.DEV && <ChatSupport />}

        {showSettings &&
          (hasOrgSettings ? (
            <Settings {...props} />
          ) : (
            <NavItem href="/settings" icon={SettingsIcon} label="Settings" />
          ))}

        {import.meta.env.DEV && <DevTools />}

        {user ? (
          <UserMenu
            user={user}
            placement="right-start"
            offset={[0, 16]}
            isOpen={userMenu.isOpen}
            onClose={userMenu.onClose}
          >
            <NavItem
              isActive={userMenu.isOpen}
              hasCollapsedTooltip={false}
              onClick={userMenu.onToggle}
            >
              <Box
                as={Avatar}
                size="sm"
                flexShrink={0}
                name={user.name || "unknown user"}
              />
              <NavItemText>
                {user.email === "guest" ? user.name : user.email}
              </NavItemText>
            </NavItem>
          </UserMenu>
        ) : (
          <NavItem hasCollapsedTooltip={false} onClick={noop}>
            <Skeleton height={32} width={32} circle style={{ flexShrink: 0 }} />
            <Skeleton height={14} width={100} />
          </NavItem>
        )}

        {showPoweredBy && <PoweredBy />}
      </Column>
    </Column>
  );
};
